import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const { FiAlertOctagon, FiRefreshCw, FiShield, FiTrash2, FiInfo, FiLock } = FiIcons;

const AdminSettings = () => {
  const { user } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState('');

  const handleRagnarok = async () => {
    if (resetKey !== 'RAGNAROK') {
      alert('Please type RAGNAROK to confirm.');
      return;
    }

    setLoading(true);
    try {
      // 1. Delete all logs
      const { error: logsError } = await supabase.from(TABLES.LOGS).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (logsError) throw logsError;

      // 2. Delete all characters
      const { error: charsError } = await supabase.from(TABLES.CHARACTERS).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (charsError) throw charsError;

      // 3. Delete all items
      const { error: itemsError } = await supabase.from(TABLES.ITEMS).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (itemsError) throw itemsError;

      // 4. Delete all users EXCEPT Super Admins
      const { error: usersError } = await supabase
        .from(TABLES.USERS)
        .delete()
        .neq('role', 'super_admin');
      
      if (usersError) throw usersError;

      alert('Ragnarok Completed. The database has been purged of all data except Super Admin accounts.');
      setIsConfirming(false);
      setResetKey('');
    } catch (err) {
      console.error(err);
      alert('Reset failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-12 pb-20 max-w-4xl mx-auto">
      <header>
        <h2 className="text-5xl font-black text-gray-900 tracking-tight">System Settings</h2>
        <p className="text-gray-400 font-bold text-lg mt-2">Administrative controls and emergency protocols.</p>
      </header>

      <div className="grid grid-cols-1 gap-8">
        {/* Security Info */}
        <div className="bento-card border-l-4 border-l-blue-500">
          <div className="flex items-start gap-6">
            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shrink-0">
              <SafeIcon icon={FiShield} className="text-2xl" />
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900">Administrative Integrity</h3>
              <p className="text-gray-500 font-medium mt-1">
                You are currently logged in as <span className="text-blue-600 font-black">@{user?.username}</span>. 
                Full system access is enabled. Exercise caution when using master reset tools.
              </p>
            </div>
          </div>
        </div>

        {/* Ragnarok Reset Card */}
        <div className="bg-white rounded-[3rem] p-10 md:p-16 border-2 border-red-50 shadow-2xl shadow-red-100/50 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-red-50 rounded-full -mr-32 -mt-32 blur-3xl opacity-50" />
          
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-8 text-red-600">
              <div className="w-12 h-12 bg-red-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-red-200">
                <SafeIcon icon={FiAlertOctagon} className="text-2xl" />
              </div>
              <h3 className="text-3xl font-black tracking-tight italic">Ragnarok</h3>
            </div>

            <div className="space-y-6 mb-12">
              <p className="text-gray-600 text-lg font-medium leading-relaxed">
                The <span className="text-red-600 font-black">Master Reset</span> protocol is the final destructive action. 
                Triggering this will permanently erase:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'All Item Drop Logs',
                  'All Master Accounts',
                  'All Pilot Accounts',
                  'All Character Rosters',
                  'All Item Price Configs',
                  'All Payment History'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-black text-gray-400 uppercase tracking-widest">
                    <SafeIcon icon={FiTrash2} className="text-red-400" /> {item}
                  </li>
                ))}
              </ul>
              <div className="p-6 bg-red-50 rounded-[2rem] border border-red-100 flex items-start gap-4">
                <SafeIcon icon={FiInfo} className="text-red-500 mt-1 shrink-0" />
                <p className="text-xs text-red-800 font-bold italic">
                  Note: Your Super Admin account and other Super Admin identities will NOT be deleted.
                </p>
              </div>
            </div>

            {!isConfirming ? (
              <button 
                onClick={() => setIsConfirming(true)}
                className="w-full py-6 bg-gray-900 text-white rounded-[2rem] font-black text-xl hover:bg-black transition-all active:scale-95 shadow-2xl flex items-center justify-center gap-4 group"
              >
                Launch Ragnarok Protocol
                <SafeIcon icon={FiRefreshCw} className="group-hover:rotate-180 transition-transform duration-700" />
              </button>
            ) : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-red-600 uppercase tracking-[0.3em] ml-4">Type "RAGNAROK" to confirm destruction</label>
                  <input 
                    type="text" 
                    value={resetKey}
                    onChange={(e) => setResetKey(e.target.value.toUpperCase())}
                    className="apple-input !bg-red-50 !text-red-600 !py-6 text-center text-2xl font-black placeholder:text-red-200 border-2 border-red-100 focus:ring-red-500"
                    placeholder="CONFIRMATION KEY"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => setIsConfirming(false)} 
                    className="flex-1 py-5 bg-gray-100 text-gray-500 rounded-[1.5rem] font-black uppercase text-xs tracking-widest hover:bg-gray-200"
                  >
                    Abort Protocol
                  </button>
                  <button 
                    onClick={handleRagnarok}
                    disabled={loading || resetKey !== 'RAGNAROK'}
                    className="flex-[2] py-5 bg-red-600 text-white rounded-[1.5rem] font-black uppercase text-xs tracking-widest hover:bg-red-700 disabled:opacity-30 disabled:scale-100 active:scale-95 transition-all shadow-xl shadow-red-200 flex items-center justify-center gap-3"
                  >
                    {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><SafeIcon icon={FiLock} /> Execute Purge</>}
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;